import React from "react";
import { Button, Space, Popconfirm, Tag } from "antd";
import { Eye, Trash2 } from "lucide-react";
import { IEnquiry } from "../../../types/api/enquiry";

interface EnquiryColumnsProps {
  onView: (record: IEnquiry) => void;
  onDelete: (id: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: "gold",
  IN_PROGRESS: "blue",
  RESOLVED: "green",
  CLOSED: "default",
  SOLD: "magenta",
};

// Captured on the enquiry form (Purchase/Valuation/Advice/Other) but
// never shown anywhere in admin - Peter, Sept 2026: "I cannot tell from
// looking at them if they are Sales or still early stage inquiries."
// Purchase is the sales-track one; the rest are earlier-stage.
export const ENQUIRY_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  purchase: { label: "Purchase", color: "green" },
  valuation: { label: "Valuation", color: "blue" },
  advice: { label: "Advice", color: "orange" },
  other: { label: "Other", color: "default" },
};

export const getEnquiryColumns = ({ onView, onDelete }: EnquiryColumnsProps) => [
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    render: (type?: string) => (
      <Tag color={type === "CONTACT_US" ? "purple" : "geekblue"}>
        {type === "CONTACT_US" ? "Contact Us" : "Sculpture Enquiry"}
      </Tag>
    ),
  },
  {
    title: "Enquiry Type",
    dataIndex: "enquiryType",
    key: "enquiryType",
    render: (enquiryType?: string) => {
      const info = enquiryType ? ENQUIRY_TYPE_LABELS[enquiryType] : undefined;
      return <Tag color={info?.color || "default"}>{info?.label || "General"}</Tag>;
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status?: string) => (
      <Tag color={STATUS_COLORS[status || "PENDING"]}>{status || "PENDING"}</Tag>
    ),
  },
  {
    title: "Customer Name",
    key: "name",
    render: (record: IEnquiry) => (
      <div>
        <div className="font-semibold text-slate-800">
          {`${record.firstName || ""} ${record.lastName || ""}`.trim()}
        </div>
        <div className="text-xs text-slate-400">{record.email}</div>
      </div>
    ),
  },
  {
    title: "Sculpture / Artist",
    key: "sculpture",
    render: (record: IEnquiry) => {
      const productTitle = record.productDoc?.title || record.artworkName;
      const artistName = record.artistDoc?.name;
      if (!productTitle && !artistName) return "-";
      return (
        <div>
          {productTitle && (
            <div className="font-medium text-slate-800">{productTitle}</div>
          )}
          {artistName && (
            <div className="text-xs text-slate-400">by {artistName}</div>
          )}
        </div>
      );
    },
  },
  {
    title: "Phone / Mobile",
    dataIndex: "phone",
    key: "phone",
    render: (phone?: string) => phone || "-",
  },
  {
    title: "Address",
    dataIndex: "addressLine",
    key: "addressLine",
    ellipsis: true,
    render: (address?: string) => address || "-",
  },
  {
    title: "City, State, Country",
    key: "location",
    render: (record: IEnquiry) => {
      const city = record.cityDoc?.name || (typeof record.cityId === "object" ? record.cityId?.name : "");
      const state = record.stateDoc?.name || (typeof record.stateId === "object" ? record.stateId?.name : "");
      const country = record.countryDoc?.name || (typeof record.countryId === "object" ? record.countryId?.name : "");
      const parts = [city, state, country].filter(Boolean);
      return parts.length > 0 ? parts.join(", ") : "-";
    },
  },
  {
    title: "Postal Code",
    dataIndex: "postalCode",
    key: "postalCode",
    render: (zip?: string) => zip || "-",
  },
  {
    title: "Message",
    dataIndex: "message",
    key: "message",
    ellipsis: true,
    render: (msg?: string) => msg || "-",
  },
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (date?: string) => (date ? new Date(date).toLocaleDateString() : "-"),
  },
  {
    title: "Actions",
    key: "actions",
    render: (record: IEnquiry) => (
      <Space size="middle">
        <Button
          type="text"
          icon={<Eye className="w-4 h-4 text-blue-600" />}
          onClick={() => onView(record)}
          title="View Details"
        />
        <Popconfirm
          title="Delete Lead"
          description="Are you sure you want to delete this enquiry lead?"
          onConfirm={() => onDelete(record._id)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="text"
            icon={<Trash2 className="w-4 h-4 text-red-500" />}
            title="Delete Lead"
          />
        </Popconfirm>
      </Space>
    ),
  },
];
